import { Inject, Injectable, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { SdkMessageHandler } from './../../../api';
import { Generator, isArray, lazyGenerator, perfMeasure, UNDEFINED_TYPE } from './../../../utils';
import { assertDependency, isAssertOn } from './../../internal/assert';
import { WchLoggerService } from './../../services/logger/wch.logger.service';
import { li, lw } from './../logger/logger.service';
import { UrlsService } from './../url/urls.service';
import { WchService } from './../wch.service';
import { SDK_MESSAGE_HANDLER } from './message';
import { Sdk } from './sdk/sdk';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'DependencyService';

@Injectable()
export class DependencyService implements OnDestroy {

  ngOnDestroy: () => void;

  private _sdk: Sdk;

  constructor(
    loggerService: WchLoggerService,
    router: Router,
    urlsService: UrlsService,
    wchService: WchService,
    @Inject(Injector) aInjector: Injector) {
    // this pointer
    const that = this;
    // measure startup
    const handle = perfMeasure(LOGGER);
    // get the multi value dependency
    const genMsgHandlers: Generator<SdkMessageHandler[]> = () => {
      // resolve the messages
      const handlers: any = aInjector.get(SDK_MESSAGE_HANDLER, []);
      const res = isArray(handlers) ? (handlers as SdkMessageHandler[]) : [(handlers as SdkMessageHandler)];
      // log this
      li(LOGGER, 'Message Handlers', res);
      // ok
      return res;
    };
    // validate the dependencies
    if (isAssertOn()) {
      // assert our dependencies
      assertDependency(aInjector, 'Injector');
    }
    // register the SDK object
    const sdk = that._sdk = new Sdk({
      loggerService,
      router,
      urlsService,
      wchService,
      msgHandlers: lazyGenerator(genMsgHandlers)
    });
    // check if we have a window
    const bWindow = (typeof window !== UNDEFINED_TYPE);
    // make this available on the window
    if (bWindow) {
      // register the SDK on the global window scope
      window[Sdk.MODULE_NAME] = sdk;
    } else {
      // log this
      lw(LOGGER, 'SDK is not available on the global scope, since there is no window object.');
    }

    that.ngOnDestroy = () => {
      // remove
      if (bWindow) {
        delete window[Sdk.MODULE_NAME];
      }
      // shut down the SDK
      sdk.destroy();
      delete that._sdk;
    };

    // done
    handle();
  }

  /*
   * Getter for the SDK
   */
  get sdk() {
    return this._sdk;
  }
}
